// Aligner compliance — the shared shape of "is this patient actually wearing their trays, and what
// do their progress photos show". One definition used by three audiences:
//   • the patient app, which produces the wear seconds and the photos;
//   • the lab console, which reviews and chases the cases that are slipping;
//   • the clinic portal, where the referring doctor reviews progress before approving.
//
// The numbers are derived, never stored: patient_wear_days holds seconds per day, and everything
// here (averages, adherence, streaks, the low-wear flag) is computed from that window so there is
// no second copy of the truth to drift.
import { z } from 'zod';

/** Days of history the compliance chart covers. */
export const COMPLIANCE_WINDOW_DAYS = 30;

/**
 * A day counts as "on target" at 90% of the daily goal — the same threshold the patient app has
 * always used to colour its own 7-day strip, so a patient is never told they hit their goal on a
 * day the lab reads as a miss.
 */
export const ON_TARGET_RATIO = 0.9;

/** Days below target, inside the last 7, before a case is flagged as low wear. */
export const LOW_WEAR_DAYS = 3;

export interface ComplianceDay {
  day: string; // YYYY-MM-DD
  seconds: number;
  /** seconds >= targetHours * 3600 * ON_TARGET_RATIO */
  onTarget: boolean;
}

/** One patient-submitted progress photo, tied to the tray it was taken on. */
export const PHOTO_POSES = ['front', 'upper', 'lower'] as const;
export type PhotoPose = (typeof PHOTO_POSES)[number];

export const PHOTO_POSE_LABELS: Record<PhotoPose, string> = {
  front: 'Front smile',
  upper: 'Upper arch',
  lower: 'Lower arch',
};

/** What the patient is asked to take, in the order the app prompts for it. */
export const PHOTO_POSE_HINTS: Record<PhotoPose, string> = {
  front: 'Aligners in, bite together, lips wide apart.',
  upper: 'Look into the camera from above - upper teeth only.',
  lower: 'Tilt your chin up - lower teeth only.',
};

/** Where a photo stands with the lab. Absent on older rows, which read as 'pending'. */
export const PHOTO_REVIEW_STATUSES = ['pending', 'approved', 'flagged'] as const;
export type PhotoReviewStatus = (typeof PHOTO_REVIEW_STATUSES)[number];

export const PHOTO_REVIEW_LABELS: Record<PhotoReviewStatus, string> = {
  pending: 'Awaiting review',
  approved: 'Looks fine',
  flagged: 'Needs attention',
};

export const photoReviewSchema = z.object({
  status: z.enum(['approved', 'flagged']),
  note: z.string().trim().max(500).optional(),
});
export type PhotoReviewInput = z.infer<typeof photoReviewSchema>;

export interface ProgressPhoto {
  id: string;
  caseId: string;
  /** The wf_case_files row holding the image; download/stream it through the usual file routes. */
  fileId: string;
  trayNo: number | null;
  pose: PhotoPose;
  note: string | null;
  takenAt: string;
  originalName: string;
  mimeType: string | null;
  sizeBytes: number;
  reviewStatus: PhotoReviewStatus;
  reviewNote: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
}

/** A photo in the lab's review queue, carrying enough case context to judge it. */
export interface PendingPhoto extends ProgressPhoto {
  caseNumber: string;
  patientName: string;
  doctorName: string | null;
}

/** Tray schedule, restated here so a compliance view needs exactly one fetch. */
export interface ComplianceTrays {
  current: number | null;
  total: number | null;
  startedAt: string | null;
  nextChangeDate: string | null;
  daysToChange: number | null;
}

export interface CaseCompliance {
  caseId: string;
  caseNumber: string;
  patientName: string;
  /** False when nobody has provisioned the patient app for this case — there is no data to judge. */
  hasPatientApp: boolean;
  targetHours: number;
  /** Oldest → newest, one entry per day, gaps filled with zero. */
  days: ComplianceDay[];
  /** Mean hours worn per day across the last 7 / 30 days. */
  avgHours7: number;
  avgHours30: number;
  /** Share of the last 7 / 30 days that met the target, 0–100. */
  adherence7: number;
  adherence30: number;
  /** Consecutive days on target, counting back from yesterday (today is still in progress). */
  streak: number;
  daysBelowTarget7: number;
  /** Last day with any recorded wear, or null if the patient has never started the timer. */
  lastWornDay: string | null;
  /** Timer running right now. */
  wearingNow: boolean;
  lowWear: boolean;
  trays: ComplianceTrays;
  photos: ProgressPhoto[];
}

/** A row in the lab's compliance queue — enough to triage without opening the case. */
export interface ComplianceQueueItem {
  caseId: string;
  caseNumber: string;
  patientName: string;
  doctorName: string | null;
  clinicName: string | null;
  hasPatientApp: boolean;
  avgHours7: number;
  adherence7: number;
  daysBelowTarget7: number;
  lastWornDay: string | null;
  wearingNow: boolean;
  lowWear: boolean;
  currentTray: number | null;
  totalTrays: number | null;
  photoCount: number;
  lastPhotoAt: string | null;
}

export interface ComplianceQueue {
  items: ComplianceQueueItem[];
  /** Cases in the queue that are currently flagged. */
  flagged: number;
  generatedAt: string;
}

/** Patient app → server, alongside the multipart image. */
export const progressPhotoSchema = z.object({
  pose: z.enum(PHOTO_POSES),
  trayNo: z.coerce.number().int().min(1).max(200).optional(),
  note: z.string().trim().max(500).optional(),
});
export type ProgressPhotoInput = z.infer<typeof progressPhotoSchema>;

/**
 * Lab → server: wear reported by phone or at a visit, entered by hand.
 *
 * The lab is not inventing data here — a patient rings in and says "I wore them about 18 hours
 * yesterday". Recording it beats leaving a hole in the chart that reads as zero.
 */
export const manualWearSchema = z.object({
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
  hours: z.coerce.number().min(0).max(24),
  note: z.string().trim().max(200).optional(),
});
export type ManualWearInput = z.infer<typeof manualWearSchema>;

/** Lab → server: nudge the patient about wear or photos. */
export const wearReminderSchema = z.object({
  kind: z.enum(['wear', 'photos']),
  message: z.string().trim().max(300).optional(),
});
export type WearReminderInput = z.infer<typeof wearReminderSchema>;

/** Lab → server: mark (or unmark) a case file as the thing the doctor is being asked to approve. */
export const shareForApprovalSchema = z.object({
  shared: z.boolean(),
  /** Optional message shown to the doctor above the player. */
  note: z.string().trim().max(500).optional(),
});
export type ShareForApprovalInput = z.infer<typeof shareForApprovalSchema>;

/**
 * The setup the lab has put in front of the doctor: a model video, a simulation, or a document.
 * `inlineUrl` streams (video seeks), `downloadUrl` saves.
 */
export interface ApprovalMedia {
  fileId: string;
  originalName: string;
  mimeType: string | null;
  sizeBytes: number;
  category: string;
  /** 'video' renders a player; anything else renders a download card. */
  isVideo: boolean;
  isImage: boolean;
  sharedAt: string | null;
  note: string | null;
  uploadedByName: string | null;
}

/** Hours worn, to one decimal — the unit every compliance surface displays. */
export const wearHours = (seconds: number): number => Math.round((seconds / 3600) * 10) / 10;

/** Plain-language verdict used for badges. Keep the wording identical across surfaces. */
export function complianceLabel(adherence: number): { label: string; tone: 'good' | 'warn' | 'bad' } {
  if (adherence >= 85) return { label: 'On track', tone: 'good' };
  if (adherence >= 60) return { label: 'Slipping', tone: 'warn' };
  return { label: 'Off track', tone: 'bad' };
}
